import type { SerializedEditorState } from 'lexical';

export type TArgument = {
	name: string;
	value: string;
};

export type ActorEventData = {
	external_id: string;
	connector_idn: string;
	event_idn: string;
	arguments: TArgument[];
	client_secret: string;
};

export type TSettings = {
	client_secret: string;
	client_website_url: string;
	color: string;
	connector_idn: string;
	avatar_id: string;
	voice_id: string;
	disable_talking_head: boolean;
	auto_open_after: number;
	greeting_phrase: string;
	agent_picture_url: string;
	agent_name: string;
	show_watermark: boolean;
	bubble_text: string;
	icon_position_x: number;
	icon_position_y: number;
	showcase_enabled: boolean;
};

export type ActorData = {
	name: string;
	external_id: string;
	connector_idn: string;
	client_secret: string;
};

export type TCommand = {
	integration_idn: string;
	connector_idn: string;
	command_idn: string;
	act_id: string;
	external_event_id: string;
	arguments: TArgument[];
};

export enum TCardSize {
	COMPACT = 'compact',
	EXTENDED = 'extended'
}

// CANVAS
export type TMentionOption = {
	value: string;
	id: string;
	color: string;
};

export type TMention = {
	type: string;
	item_idn: string;
};

export type TGroupItem = {
	idn: string;
	origin_idn?: string;
	title: string;
	description?: string;
	body: string;
	version?: string;
	is_from_library?: boolean;
	mentions: TMention[];
};

export type TTypeGroup = {
	idn: string;
	title: string;
	color: string;
	description?: string;
	items: TGroupItem[];
};

export type TCanvas = {
	title: string;
	types: TTypeGroup[];
};

// LIBRARY

export type TLibraryItem = {
	idn: string;
	title: string;
	body: string;
	description: string;
	version: string;
	mentions: TMention[];
};

export type TLibraryGroup = {
	idn: string;
	title: string;
	color: string;
	items: TLibraryItem[];
};
export type TLibrary = TLibraryGroup[];

export type TGroupItemFrontend = Omit<TGroupItem, 'title' | 'body'> & {
	title: SerializedEditorState;
	body: SerializedEditorState;
};

export type TTypeGroupFrontend = Omit<TTypeGroup, 'items'> & {
	items: TGroupItemFrontend[];
};

export type TCanvasFrontend = Omit<TCanvas, 'types'> & {
	types: TTypeGroupFrontend[];
};

export type TLibraryItemFrontend = Omit<TLibraryItem, 'title' | 'body'> & {
	title: SerializedEditorState;
	body: SerializedEditorState;
};

export type TLibraryGroupFrontend = Omit<TLibraryGroup, 'items'> & {
	items: TLibraryItemFrontend[];
};

export type TLibraryFrontend = TLibraryGroupFrontend[];

// FLOW
export type TFlowHeading = {
	id: string;
	source: Omit<TTypeGroup, 'items'>;
};

export type TFlowItem = {
	incomingMentions: TMention[];
	outgoingMentions: TMention[];
	brokenMentions: TMention[];
	color: string;
	cellX: number;
	cellY: number;
	type: string;
	isCollapsed: boolean;
	cardHeight: number;
	source: TGroupItem;
};

export type TFlowItemFrontend = Omit<TFlowItem, 'source'> & {
	source: TGroupItemFrontend;
};

export interface CanvasItem {
	idn: string;
	title: string;
	body: string;
	description?: string;
	mentions: TMention[];
	is_from_library?: boolean;
	origin_idn?: string;
}

export interface CanvasItemFrontend extends Omit<CanvasItem, 'title' | 'body'> {
	title: SerializedEditorState;
	body: SerializedEditorState;
}

export type CanvasSectionConfig = {
	idn: string;
	title: string;
	color: string;
	description?: string;
};

export type ChangeSet = {
	previous: string;
	current: string;
};

export type CanvasSections = Record<string, CanvasItem[]>;
export type CanvasSectionConfigs = Record<string, CanvasSectionConfig>;
export type ParsedCanvas = {
	sections: CanvasSections;
	configs: CanvasSectionConfigs;
};

export type CanvasSectionsFrontend = Record<string, CanvasItemFrontend[]>;
export type ParsedCanvasFrontend = {
	sections: CanvasSectionsFrontend;
	configs: CanvasSectionConfigs;
};

export type TAccount = {
	id: string;
	idn: string;
	email: string;
	name: string;
	scopes: string[];
};
